'use client'

import { useEffect, useState } from 'react'

import { useUserAccount } from '../providers/UserAccountProvider'

type PartialWalletConnectProvider = {
  session: { peer: { metadata: { name: string } } }
}

// Safe apps announce themselves as "Safe{Wallet}" (web) or "Safe{Mobile}" (iOS/Android).
const SAFE_WALLET_CONNECT_NAME_PREFIX = 'Safe{'

// Resolves the WalletConnect peer metadata name (the app the user actually
// connected with — e.g. "Rabby", "Rainbow", "Safe{Wallet}", "Safe{Mobile}")
// when the active connector is WalletConnect, otherwise returns undefined.
export function useWalletConnectMetadata() {
  const [walletName, setWalletName] = useState<string | undefined>(undefined)
  const { connector, userAddress } = useUserAccount()

  useEffect(() => {
    let isActive = true

    if (!connector?.getProvider || connector.id !== 'walletConnect') {
      setWalletName(undefined)
      return
    }

    connector.getProvider().then(
      provider => {
        const walletConnectProvider = provider as PartialWalletConnectProvider
        try {
          if (isActive) setWalletName(walletConnectProvider.session.peer.metadata.name)
        } catch {
          if (isActive) setWalletName(undefined)
        }
      },
      () => {
        if (isActive) setWalletName(undefined)
      }
    )

    return () => {
      isActive = false
    }
    // Re-run on address changes because wagmi reuses one WalletConnect connector across sessions.
  }, [connector, userAddress])

  const isSafeAccountViaWalletConnect =
    walletName?.startsWith(SAFE_WALLET_CONNECT_NAME_PREFIX) ?? false

  return { isSafeAccountViaWalletConnect, walletConnectPeerName: walletName }
}
